import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Button,
  Drawer,
  CircularProgress,
} from "@mui/material";
import {
  GridActionsCellItem,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import MUITypography from "../../Components/MUITypography";
import MUITextFieldSelect from "../../Components/MUITextFieldSelect";
import { VM_sub_venueTypes } from "../../Helper/RegistrationHelper";
import {
  API_SUB_VENUE_REGISTRATION,
  API_UPDATE_SUB_VENUE_REGISTRATION,
} from "../../Util/URLConstants";
import { postApi } from "../../Util/ApiService";
import { toast } from "react-toastify";
import {
  align_center,
  align_column,
  align_right,
  dataGridStyle,
  VM_Colors,
} from "../../Styles/VMStyles";
import StyledInputField from "../../Components/Custom/StyledInputField";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import StorageFactory from "../../Util/StorageFactory";
import { ACCESS_RESTRICTED_MESSAGE, SESSION_USER_ROLES_PERMISSIONS } from "../../Util/VMConstants";
import { checkIsPermissionExist, renderSNo, toTitleCaseInput } from "../../Util/UtilHelper";
import NoPermission from "../../Components/NoPermission";
import ReusableDataGrid from "../../Components/ReusableDataGrid";

const initValues = {
  sub_venue_name: "",
  sub_venue_type: "",
  sub_venue_capacity: "",
  sub_venue_status: "Active", // default to Active
  custom_sub_venue_type: "",
};

function UpdateSubvenueDetails({ subVenueState, venueState, getUserInfo, loadProfile }) {
  const [openSubVenueDialog, setOpenSubVenueDialog] = useState(false);
  const [state, setState] = useState(initValues);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const sessionFactory = new StorageFactory("session");
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  // 🔑 Sync DataGrid rows with latest subVenueState
  useEffect(() => {
    if (subVenueState) {
      const updatedRows = subVenueState.map((item, index) => ({
        ...item,
        s_no: index + 1,
        id: item.sub_venue_id ?? index, // ensure unique id
      }));
      setRows(updatedRows);
    }
  }, [subVenueState]);

  const handleEditClick = (id) => () => {
    const rowToEdit = rows.find((row) => row.id === id);

    if (rowToEdit) {
      const isCustomType = !VM_sub_venueTypes.some(
        (type) => type.value === rowToEdit.sub_venue_type
      );

      setState({
        sub_venue_name: rowToEdit.sub_venue_name,
        sub_venue_type: isCustomType ? "Others" : rowToEdit.sub_venue_type,
        sub_venue_capacity: rowToEdit.sub_venue_capacity,
        sub_venue_status: rowToEdit.sub_venue_status,
        custom_sub_venue_type: isCustomType ? rowToEdit.sub_venue_type : "",
      });

      setOpenSubVenueDialog(true);
      setIsEditMode(true);
      setEditRowId(id);
    }
  };

  const handleSubmit = async () => {
    const {
      sub_venue_name,
      sub_venue_type,
      sub_venue_capacity,
      sub_venue_status,
      custom_sub_venue_type,
    } = state;

    if (!sub_venue_name || !sub_venue_type || !sub_venue_capacity || !sub_venue_status) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (sub_venue_type === "Others" && !custom_sub_venue_type.trim()) {
      toast.error("Please enter a custom sub venue type.");
      return;
    }

    const finalType = sub_venue_type === "Others" ? custom_sub_venue_type : sub_venue_type;
    setLoading(true);

    try {
      if (isEditMode && editRowId !== null) {
        // 🔄 Update Subvenue
        const updatedObj = {
          sub_venue_id: rows.find((r) => r.id === editRowId)?.sub_venue_id,
          sub_venue_name,
          sub_venue_type: finalType,
          sub_venue_capacity,
          sub_venue_status,
        };

        const response = await postApi(API_UPDATE_SUB_VENUE_REGISTRATION, updatedObj);
        setLoading(false);
        if (response.status_code === 200) {
          toast.success("Subvenue updated successfully");
          resetForm();
          if (getUserInfo) getUserInfo();
        } else {
          toast.error("Update failed");
        }
      } else {
        // ➕ Create Subvenue
        const obj = {
          user_id: venueState.user_id,
          venue_id: venueState.venue_id,
          sub_venues: [
            {
              sub_venue_name,
              sub_venue_type: finalType,
              sub_venue_capacity,
              sub_venue_status,
            },
          ],
        };

        const resp = await postApi(API_SUB_VENUE_REGISTRATION, obj);
        setLoading(false);
        if (resp.status_code === 200) {
          toast.success("Subvenue added successfully");
          resetForm();
          getUserInfo();
          loadProfile();
        } else {
          toast.error("Sub venue registration failed. Please try again.");
        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Submit Error:", error);
    }
  };

  const resetForm = () => {
    setOpenSubVenueDialog(false);
    setState(initValues);
    setIsEditMode(false);
    setEditRowId(null);
  };

  const columns = [
        renderSNo(),
    { field: "sub_venue_name", headerName: "Name", flex: 1 ,sortable: false },
    { field: "sub_venue_type", headerName: "Type", flex: 1 ,sortable: false },
    { field: "sub_venue_capacity", headerName: "Capacity", type: "number", flex: 0.6, sortable: false  },
    { field: "sub_venue_status", headerName: "Status", flex: 1,sortable: false },
    // {
    //   field: "actions",
    //   type: "actions",
    //   headerName: "Actions",
    //   flex: 1,
    //   getActions: ({ id }) => [
    //     <GridActionsCellItem
    //       icon={<EditIcon />}
    //       label="Edit"
    //       onClick={handleEditClick(id)}
    //       color="inherit"
    //     />,
    //   ],
    // },
  ];

  if (checkIsPermissionExist(VM_PERMISSIONS, "VM021")) {
    columns.push({
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      getActions: ({ id }) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={handleEditClick(id)}
          color="inherit"
        />,
      ],
    });
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MUITypography variant="h4" gutterBottom   sx={{   fontWeight: 700, color: '#1E1E1E' }} >Sub Venue Details</MUITypography>
        <Box sx={align_right}>
          {checkIsPermissionExist(VM_PERMISSIONS, "VM017") && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setOpenSubVenueDialog(true);
                setState(initValues);
                setIsEditMode(false);
                setEditRowId(null);
              }}
              startIcon={<AddIcon />}
            >
              Add Sub Venue
            </Button>
          )}
        </Box>
      </Box>

      {checkIsPermissionExist(VM_PERMISSIONS, "VM015") ? (
        <ReusableDataGrid
          rows={rows}
          columns={columns}
          page={page}
          pageSize={pageSize}
          rowCount={rows.length}
          paginationMode="client"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 25]}
          autoHeight
          customSx={dataGridStyle}
        />
      )
    :  <Box sx={{ mt: 4 }}>
            <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />  
        </Box>
    }

      <Drawer
        anchor="right"
        open={openSubVenueDialog}
        onClose={() => setOpenSubVenueDialog(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: "480px" }, p: 3 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MUITypography sx={{ fontSize: "1.2rem", fontWeight: 600 }}>
            {isEditMode ? "Edit Sub Venue" : "Add Sub Venue"}
          </MUITypography>
          <IconButton onClick={() => setOpenSubVenueDialog(false)}>
            <CancelIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <StyledInputField
            heading="Sub Venue Name"
            name="subVenueName"
            placeholder="Enter Sub Venue Name"
            value={state.sub_venue_name}
            onChange={(e) =>
              setState({
                ...state,
                sub_venue_name: toTitleCaseInput(e.target.value),
              })
            }
            fullWidth
          />

          <Box>
            <MuiTypographyMandatory label="Sub Venue Type" required />
            <MUITextFieldSelect
              name="subVenueType"
              id="subVenueType"
              required
              value={state.sub_venue_type}
              onChange={(e) =>
                setState({
                  ...state,
                  sub_venue_type: e.target.value,
                  custom_sub_venue_type: "",
                })
              }
              options={VM_sub_venueTypes}
              sx={{ width: "100%" }}
            />
          </Box>

          {state.sub_venue_type === "Others" && (
            <StyledInputField
              heading="Custom Sub Venue Type"
              name="customSubVenueType"
              placeholder="Enter custom type"
              value={state.custom_sub_venue_type}
              onChange={(e) =>
                setState({ ...state, custom_sub_venue_type: e.target.value })
              }
              fullWidth
            />
          )}

          <StyledInputField
            heading="Capacity"
            name="capacity"
            type="number"
            placeholder="Enter Capacity"
            value={state.sub_venue_capacity}
            onChange={(e) =>
              setState({ ...state, sub_venue_capacity: e.target.value })
            }
            fullWidth
          />

          <Box sx={align_column}>
            <MuiTypographyMandatory label="Sub Venue Status" required />
            <MUITextFieldSelect
              name="subVenueStatus"
              id="subVenueStatus"
              required={true}
              value={state.sub_venue_status}
              options={[
                { label: "Active (Default)", value: "Active" },
                { label: "Inactive", value: "Inactive" },
              ]}
              onChange={(e) =>
                setState({ ...state, sub_venue_status: e.target.value })
              }
              sx={{ width: "100%" }}
            />

            {state.sub_venue_status === "Inactive" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                  color: "warning.main",
                }}
              >
                <ErrorIcon fontSize="small" />
                <MUITypography variant="body2">
                  This Subvenue will not be available for booking.
                </MUITypography>
              </Box>
            )}
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          <Button onClick={() => setOpenSubVenueDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {isEditMode ? "Update" : "Add"}
          </Button>
        </Box>
      </Drawer>

      <Box sx={{ position: "relative" ,...align_center}}>
        {loading ? <CircularProgress loading={loading} /> : null}
      </Box>
    </Box>
  );
}

export default UpdateSubvenueDetails;
